// handles inputs for all types of arrays (tuple[], uint[], string[] etc..)
import type { FC } from "react"
import React, { useCallback, useEffect, useState } from "react"
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Stack,
  Text,
} from "@chakra-ui/react"
import type { FieldProps } from "formik"
import { Field, useFormikContext } from "formik"
import type { AbiInput } from "web3-utils"

import SimpleValueInput from "createProposal/components/actions/SimpleValueInput"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { encodeComplexField } from "createProposal/helpers/abi"
import SimpleArrayValuesInputs from "createProposal/components/actions/SimpleArrayValuesInputs"
import RedCircleX from "ui/components/icons/RedCircleX"

type CreateProposalCustomInputProps = {
  label: string
  name: string
  placeholder: string
  validate?: (value?: string) => string | undefined
  components?: AbiInput[]
  type: string
}

// initial value is an array that has one single empty string item, in order to show it as an input (or group of inputs in the case of tuples)
const INITIAL_VALUE = [""]

const CreateProposalValueArrayInput: FC<CreateProposalCustomInputProps> = ({
  label,
  name,
  placeholder,
  validate,
  components,
  type,
}) => {
  const [inputValues, setInputValues] = useState<string[]>(INITIAL_VALUE)

  const [touchedField, setTouchedField] = useState<boolean>(false)

  const addInputFields = useCallback(() => {
    setInputValues((prevInputValues) => [...prevInputValues, ""])
  }, [])

  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  useEffect(() => {
    setInputValues(INITIAL_VALUE)
  }, [type])

  useEffect(() => {
    try {
      if (inputValues.length === 0) {
        // Handle empty array case
        setFieldValue(name, "[]")

        return
      }

      for (let index = 0; index < inputValues.length; index++) {
        const simpleType = type.replace("[]", "")

        encodeComplexField(components, simpleType, inputValues[index])

        if (inputValues[index].length < 1)
          // Refuse empty values, can't build a proper array with them
          throw "Empty value in one of the entries."
      }

      const combinedValues = ["string", "address", "byte", "int"].some((word) =>
        type.includes(word),
      )
        ? `[${inputValues.map((value) => `"${value}"`).join(", ")}]`
        : `[${inputValues.join(", ")}]` // this is for tuples, boolean etc., no quotes needed

      setFieldValue(name, combinedValues)
    } catch (error) {
      const combinedValues = ``

      setFieldValue(name, combinedValues)
    }
  }, [inputValues, name, components, setFieldValue, type])

  const updateInputValues = (newValue: string, inputIndex: number) => {
    setInputValues((prevArray) => {
      const newArray = [...prevArray]
      newArray[inputIndex] = newValue

      return newArray
    })
  }

  const handleChangeValue = useCallback((newValue: string, index: number) => {
    setTouchedField(true)
    updateInputValues(newValue, index)
  }, [])

  const displayInputFields = useCallback(
    (removeInputValueAtIndex: (index: number) => void) => {
      const inputsToShow = inputValues.map((value, index) =>
        components ? (
          <SimpleValueInput
            key={`inputs-${index}`}
            shouldIndentLabel
            changeInputValue={(newValue) => {
              handleChangeValue(newValue, index)
            }}
            components={components}
            indexInArray={index}
          />
        ) : (
          <SimpleArrayValuesInputs
            key={`inputs-${index}`}
            changeInputValue={(newValue) => {
              handleChangeValue(newValue, index)
            }}
            index={index}
            removeInputValueAtIndex={removeInputValueAtIndex}
            type={type.replace("[]", "")}
            value={inputValues[index]}
          />
        ),
      )

      return inputsToShow
    },
    [inputValues, components, handleChangeValue, type],
  )

  const removeLastInputValue = () => {
    if (inputValues.length > 1)
      setInputValues((prevArray) => prevArray.slice(0, -1))
  }

  const removeInputValueAtIndex = (index: number) => {
    if (index < 0) return
    setInputValues((prevArray) => {
      return prevArray.filter((_, i) => i !== index)
    })
  }

  return (
    <Field name={name} validate={validate}>
      {({ meta }: FieldProps) => {
        const isInvalid = Boolean(touchedField && meta.error)

        return (
          <FormControl isInvalid={isInvalid}>
            <HStack h={10}>
              <FormLabel
                alignItems="center"
                bg="purple.50"
                border="gray.dark"
                color="purple.600"
                display="flex"
                h="full"
                justifyContent="center"
                m={0}
                p={1}
                w={166.06}
              >
                <Text textStyle="body.bold.lg" w="auto">
                  {label}
                </Text>
              </FormLabel>
              <Text color="gray.500">{placeholder}</Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  addInputFields()
                }}
              >
                Add item
              </Button>
            </HStack>
            <Stack my="0.5rem">
              {inputValues.length > 0 ? (
                displayInputFields(removeInputValueAtIndex)
              ) : (
                <Text color="gray.500">
                  No items to display. Please add one or it will be saved as an
                  empty array.
                </Text>
              )}
            </Stack>

            {type.startsWith("tuple") ? (
              <HStack>
                <Button
                  isDisabled={inputValues.length <= 1}
                  variant="outline"
                  width="100%"
                  onClick={removeLastInputValue}
                >
                  Remove last item
                </Button>
              </HStack>
            ) : null}

            {isInvalid ? (
              <FormHelperText alignItems="center" color="purple.900" mt={1.5}>
                <Icon as={RedCircleX} /> Some entries are either invalid or
                haven&apos;t been filled out yet
              </FormHelperText>
            ) : null}
          </FormControl>
        )
      }}
    </Field>
  )
}

export default CreateProposalValueArrayInput
